/**
 * CalibrationConsensusView — E4-S2 REVIEW-section view.
 *
 * Consumes GET /calibration-sessions/:id/consensus. Shows per-evaluator
 * scores, spread, and a converged/divergent badge.
 *
 * Same framing as InterRaterView: this is calibration, not surveillance.
 * The header copy carries the framing.
 */

import { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import { apiFetch } from '../providers/api-rest-provider';

interface ConsensusResponse {
  session_id: string;
  event_id: string;
  performance_ref: string;
  status: string;
  n_evaluators: number;
  n_expected: number;
  n_pending: string[];
  perEvaluator: { recorded_by: string; score: number }[];
  mean: number | null;
  stddev: number | null;
  spread: number | null;
  converged: boolean | null;
}

interface ConsensusRequestState {
  key: string;
  data: ConsensusResponse | null;
  loading: boolean;
  error: string | null;
}

export function CalibrationConsensusView() {
  const [sessionId, setSessionId] = useState('');
  const session = sessionId.trim();
  const [request, setRequest] = useState<ConsensusRequestState | null>(null);
  const currentRequest = request?.key === session ? request : null;
  const data = currentRequest?.data ?? null;
  const loading = currentRequest?.loading ?? Boolean(session);
  const error = currentRequest?.error ?? null;

  useEffect(() => {
    if (!session) return;
    const controller = new AbortController();
    const timer = window.setTimeout(async () => {
      setRequest({ key: session, data: null, loading: true, error: null });
      try {
        const raw = await apiFetch(
          `calibration-sessions/${encodeURIComponent(session)}/consensus`,
          { signal: controller.signal },
        ) as ConsensusResponse;
        if (controller.signal.aborted) return;
        if (raw.session_id !== session) {
          throw new Error('Consensus response does not match the requested session.');
        }
        setRequest({ key: session, data: raw, loading: false, error: null });
      } catch (err) {
        if (controller.signal.aborted) return;
        setRequest({
          key: session,
          data: null,
          loading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      }
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [session]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <GroupsIcon color="primary" />
        <Typography variant="h5">Calibration consensus</Typography>
        <Chip label="E4-S2" size="small" color="warning" />
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>Calibration, not surveillance</AlertTitle>
        Multiple cadre score the same performance. This view shows how far
        apart the scores landed and whether the group converged — the point
        is to run a refresher on rubric interpretation, not to rank raters.
      </Alert>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="session_id"
            fullWidth
            value={sessionId}
            onChange={(e) => setSessionId(e.target.value)}
            helperText="Required. Calibration session UUID."
          />
        </CardContent>
      </Card>

      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && !loading && (
        <>
          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <Chip label={`event: ${data.event_id}`} size="small" />
            <Chip label={`performance: ${data.performance_ref}`} size="small" />
            <Chip label={`status: ${data.status}`} size="small" color={data.status === 'closed' ? 'default' : 'primary'} />
            <Chip
              label={data.converged === null ? 'converged: pending' : data.converged ? 'CONVERGED' : 'DIVERGENT'}
              size="small"
              color={data.converged === null ? 'default' : data.converged ? 'success' : 'error'}
            />
            <Chip label={`n=${data.n_evaluators}/${data.n_expected}`} size="small" />
            {data.mean !== null && <Chip label={`mean: ${data.mean}`} size="small" />}
            {data.spread !== null && <Chip label={`spread: ${data.spread}`} size="small" />}
            {data.stddev !== null && <Chip label={`σ: ${data.stddev}`} size="small" />}
          </Stack>

          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Evaluator</TableCell>
                  <TableCell align="right">Score (0–100)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.perEvaluator.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Typography variant="body2" color="text.secondary">
                        No scores submitted yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  data.perEvaluator.map((e) => (
                    <TableRow key={e.recorded_by}>
                      <TableCell>{e.recorded_by}</TableCell>
                      <TableCell align="right">{e.score}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>

          {data.n_pending.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <AlertTitle>Pending evaluators</AlertTitle>
              {data.n_pending.join(', ')}
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
